/**
 * 상담 대시보드 페이지 (통합 버전)
 * Consultation Dashboard Page (Integrated Version)
 *
 * AI 인사이트, 상담 효과 통계, 최근 상담 기록을 한눈에 볼 수 있는 통합 대시보드
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AIInsightCard from '../components/AIInsightCard';
import ConsultationEffectivenessWidget from '../components/ConsultationEffectivenessWidget';
import { counselingRecordService } from '../../learning/services/CounselingRecordService';
import { API_BASE_URL } from '../../../config/api';
import './ConsultationDashboardPage.css';

interface ConsultationDashboardPageProps {
  organizationId?: string;
}

const ConsultationDashboardPage: React.FC<ConsultationDashboardPageProps> = ({ organizationId: propOrgId }) => {
  const navigate = useNavigate();
  const [organizationId, setOrganizationId] = useState<string>('');
  const [recentRecords, setRecentRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadOrganizationInfo();
  }, [propOrgId]);

  const loadOrganizationInfo = async () => {
    try {
      // Props에서 먼저 확인
      if (propOrgId) {
        setOrganizationId(propOrgId);
        loadRecentRecords(propOrgId);
        return;
      }

      // localStorage에서 확인
      const storedOrgId = localStorage.getItem('organizationId');
      if (storedOrgId) {
        setOrganizationId(storedOrgId);
        loadRecentRecords(storedOrgId);
        return;
      }

      // API에서 현재 사용자 정보 가져오기
      const authToken = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/v1/supabase-auth/me`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        const orgId = userData.organization_id;

        if (orgId) {
          setOrganizationId(orgId);
          // ⭐ 표준 키 'organization_id'로 저장 (다른 페이지·서비스가 이 키를 읽음)
          localStorage.setItem('organization_id', orgId);
          loadRecentRecords(orgId);
        } else {
          setError('조직 정보를 찾을 수 없습니다.');
        }
      } else {
        setError('사용자 정보를 가져오는데 실패했습니다.');
      }
    } catch (err) {
      console.error('조직 정보 로딩 실패:', err);
      setError('조직 정보를 가져오는 중 오류가 발생했습니다.');
    }
  };

  const loadRecentRecords = async (orgId: string) => {
    try {
      setLoading(true);
      const records = await counselingRecordService.getByOrganization(orgId);

      // 최근 5개만 표시
      const sorted = records
        .sort((a, b) => new Date(b.counseling_date).getTime() - new Date(a.counseling_date).getTime())
        .slice(0, 5);

      setRecentRecords(sorted);
    } catch (error) {
      console.error('최근 상담 기록 로딩 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      regular: '정기 상담',
      special: '특별 상담',
      parent: '학부모 상담',
      academic: '학업 상담',
      counseling: '심리 상담',
    };
    return labels[type] || type;
  };

  const handleRecordClick = (recordId: string) => {
    // 상담 기록 상세 페이지로 이동
    navigate(`/owner/consultations/${recordId}`);
  };

  const handleViewAllRecords = () => {
    navigate('/owner/consultations');
  };

  const handleCreateConsultation = () => {
    navigate('/owner/consultations/new');
  };

  if (error) {
    return (
      <div className="consultation-dashboard-page">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>다시 시도</button>
        </div>
      </div>
    );
  }

  if (!organizationId && !loading) {
    return (
      <div className="consultation-dashboard-page">
        <div className="loading-state">조직 정보를 불러오는 중...</div>
      </div>
    );
  }

  return (
    <div className="consultation-dashboard-page">
      <div className="dashboard-header">
        <h1 className="page-title">상담 대시보드 <span style={{ fontSize: 14, color: '#f59e0b', fontWeight: 600, marginLeft: 8 }}>· 준비중</span></h1>
        <button className="create-button" onClick={handleCreateConsultation}>
          + 새 상담 작성
        </button>
      </div>

      {/* ⭐ 서비스 준비 중 안내 — AI 정비 중이라 일부 기능 비활성 */}
      <div style={{
        margin: '12px 0 20px',
        padding: '14px 18px',
        backgroundColor: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: 10,
        color: '#92400e',
        fontSize: 14,
      }}>
        ⚠️ 이 페이지는 현재 <b>서비스 준비 중</b>입니다. AI 인사이트는 정비 후 다시 제공 예정이며, 그동안 상담 효과 통계·최근 기록 위주로 확인하실 수 있습니다.
      </div>

      <div className="dashboard-content">
        {/* 2열 그리드 레이아웃 */}
        <div className="dashboard-grid">
          {/* 왼쪽 컬럼: AI 인사이트 — 준비 중 임시 비활성 */}
          {/* <div className="grid-left">
            <AIInsightCard organizationId={organizationId} />
          </div> */}

          {/* 오른쪽 컬럼: 상담 효과 통계 */}
          <div className="grid-right">
            <ConsultationEffectivenessWidget organizationId={organizationId} monthsToShow={6} />
          </div>
        </div>

        {/* 전체 폭: 최근 상담 기록 */}
        <div className="recent-records-section">
          <div className="section-header">
            <h2 className="section-title">📋 최근 상담 기록</h2>
            <button className="view-all-button" onClick={handleViewAllRecords}>
              전체 보기 →
            </button>
          </div>

          {loading ? (
            <div className="loading-state">로딩 중...</div>
          ) : recentRecords.length === 0 ? (
            <div className="empty-state">
              <p>아직 작성된 상담 기록이 없습니다.</p>
              <button className="create-button-secondary" onClick={handleCreateConsultation}>
                첫 상담 작성하기
              </button>
            </div>
          ) : (
            <div className="records-list">
              {recentRecords.map((record) => (
                <div
                  key={record.id}
                  className="record-card"
                  onClick={() => handleRecordClick(record.id)}
                >
                  <div className="record-header">
                    <div className="record-meta">
                      <span className="record-date">{formatDate(record.counseling_date)}</span>
                      <span className="record-type-badge">{getTypeLabel(record.consultation_type)}</span>
                    </div>
                    {record.effectiveness_score !== null && record.effectiveness_score !== undefined && (
                      <div className="effectiveness-badge">
                        효과: {record.effectiveness_score.toFixed(0)}점
                      </div>
                    )}
                  </div>
                  <div className="record-content">
                    <h3 className="student-name">학생: {record.student_name || '이름 없음'}</h3>
                    <p className="record-summary">
                      {record.consultation_summary || '요약 내용이 없습니다.'}
                    </p>
                  </div>
                  {record.topics && record.topics.length > 0 && (
                    <div className="record-topics">
                      {record.topics.slice(0, 3).map((topic: string, index: number) => (
                        <span key={index} className="topic-tag">
                          {topic}
                        </span>
                      ))}
                      {record.topics.length > 3 && (
                        <span className="topic-tag more">+{record.topics.length - 3}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsultationDashboardPage;
